import { useState } from 'react';
import { Button, Flex, DropzoneArea } from '@instamenu/ui-system-next';
import { ChatAttachments } from './chat-attachments';
import type { AttachmentInput } from '../thread/attachment-utils';
import { inferMimeType } from '../thread/attachment-utils';
import * as styles from './message-edit.css';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import type { ChatHistoryItem } from '../types';
import type { Thread } from '../thread/thread';
import { CameraCapture } from './camera-capture';
import { AutoResizeTextarea } from './auto-resize-textarea';
import { collectAttachmentsFromUserTurn } from '../utils/collect-attachments';
import { useLocale } from '@instamenu/context-i18n';

type MessageEditProps = {
  item: ChatHistoryItem;
  thread: Thread;
  onClose: () => void;
  placeholder?: string;
};

export const MessageEdit = ({
  item,
  thread,
  onClose,
  placeholder = 'Edit message',
}: MessageEditProps) => {
  const { i18n } = useLocale();
  if (item.type !== 'message' || item.content.type !== 'text') {
    throw new Error('MessageEdit can only edit text messages');
  }

  const initialMessage = item.content.text;
  const messageIndex = thread.history.findIndex((h) => h.id === item.id);
  const initialAttachments = collectAttachmentsFromUserTurn(thread, messageIndex);

  const [message, setMessage] = useState(initialMessage);
  const [attachments, setAttachments] = useState<AttachmentInput[]>(initialAttachments);

  const handleSave = () => {
    const messageIndex = thread.history.findIndex((h) => h.id === item.id);

    // Find the start of the user turn (before any attachments)
    let userTurnStartIndex = messageIndex;
    for (let i = messageIndex - 1; i >= 0; i--) {
      const historyItem = thread.history[i];
      if (historyItem.role !== 'user') break;
      userTurnStartIndex = i;
    }

    thread.capturePromptEditRerun({
      originalMessage: initialMessage,
      editedMessage: message,
    });

    onClose();
    thread.resetToIndex(userTurnStartIndex);
    thread.post({ message, attachments });
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <DropzoneArea
      setFile={(base64, filename) => {
        const mimeType = inferMimeType(base64);
        if (!base64.startsWith('data:') || !filename) return;
        setAttachments((prev) => [...prev, { data: base64, mimeType, name: filename }]);
      }}
    >
      <div className={styles.messageEditRoot}>
        <ChatAttachments attachments={attachments} onRemove={handleRemoveAttachment} />
        <div className={styles.messageEditRow}>
          <AutoResizeTextarea
            autoFocus
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onSubmit={handleSave}
            disabled={thread.isBusy}
            placeholder={placeholder}
            className={styles.messageEditTextarea}
          />
        </div>
        <Flex css={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <CameraCapture
            disabled={thread.isBusy}
            onCapture={(data) => setAttachments((prev) => [...prev, data])}
          />
          <Flex css={{ justifyContent: 'flex-end', gap: themeVars.pad[12] }}>
            <Button variant="secondary" onClick={onClose} disabled={thread.isBusy}>
              {i18n.t('generics.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={thread.isBusy}>
              {i18n.t('generics.send')}
            </Button>
          </Flex>
        </Flex>
      </div>
    </DropzoneArea>
  );
};
